import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useInquiryList } from '../../hooks/useInquiry';
import type { InquiryStatus, InquiryCategory } from '../../types/report';
import { formatDateKST } from '../../utils/dateUtils';

const STATUS_CONFIG: Record<InquiryStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  SUBMITTED: { label: '접수됨', color: 'default' },
  IN_PROGRESS: { label: '처리 중', color: 'primary' },
  REPLIED: { label: '답변 완료', color: 'success' },
  CLOSED: { label: '종결', color: 'default' },
  REOPENED: { label: '추가 문의', color: 'warning' },
};

const CATEGORY_LABELS: Record<InquiryCategory, string> = {
  ACCOUNT: '계정/프로필',
  PAYMENT: '결제/환불',
  PRIVACY: '개인정보',
  TECHNICAL: '기술 문제',
  REPORT: '신고하기',
  OTHER: '기타',
};

const InquiryListPage: React.FC = () => {
  const navigate = useNavigate();
  const { inquiries, loading, error } = useInquiryList();


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>문의 내역</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && inquiries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mb: 3 }}>
              접수된 문의가 없습니다.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/support/inquiry')}
              sx={{ py: 1.5, px: 4, fontSize: '1rem', fontWeight: 700 }}
            >
              1:1 문의하기
            </Button>
          </Box>
        )}

        {inquiries.map((inquiry) => {
          const status = STATUS_CONFIG[inquiry.status];
          return (
            <Card
              key={inquiry.id}
              elevation={0}
              sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <CardActionArea onClick={() => navigate(`/support/inquiry/${inquiry.id}`)}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={CATEGORY_LABELS[inquiry.category]}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.8rem', height: 24 }}
                      />
                      <Chip
                        label={status.label}
                        size="small"
                        color={status.color}
                        sx={{ fontSize: '0.8rem', height: 24 }}
                      />
                    </Box>
                    <ChevronRight sx={{ color: 'text.secondary', flexShrink: 0 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5, lineHeight: 1.4 }}>
                    {inquiry.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    {formatDateKST(inquiry.createdAt)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}

        {inquiries.length > 0 && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/support/inquiry')}
              sx={{ py: 1.5, px: 4, fontSize: '1rem', fontWeight: 600 }}
            >
              새 문의 작성
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default InquiryListPage;
